import axios from "axios";
import { amRoutes, pencilerRoles, ucEndpointsUrls, ucEndpoints } from "../../config/config";
import {
  delayBeforeRedirect,
  httpStatusCode,
  delayBeforeHideSuccessMessage
} from "../../config/constants";
import { USER_CREDENTIALS_MISSING_2FA_CODE } from "../../config/error_codes";
import { AppDispatch, RootState } from "../../reducers";
import { history } from "../../store";
import { resetInviteUserToAccountModal, setOrganization } from "../../store/accounts";
import { resetApp } from "../../store/appStatus";
import * as statusMessages from "../../store/statusMessages";
import {
  authFail,
  authSuccess,
  getAclListFail,
  getAclListSuccess,
  loginFail,
  loginStart,
  loginSuccess,
  logoutUser,
  resetPasswordFailed,
  resetResetPasswordModal,
  sendGetAclList,
  setAvailableRegions,
  setAvailableRoles,
  setUserClaims,
  setUserTypes,
  setToken as setTokenAction,
  show2FALoginField,
  startAuth
} from "../../store/users";
import { setUserSettings } from "../../store/userSettings";
import { Organization } from "../../types/organizations";
import { Role } from "../../types/roles";
import { UserClaims } from "../../types/users";
import {
  exists,
  getApplicationErrorFromRequest,
  getErrorMessageFromRequest
} from "../../utils/utils";
import { getUsers } from "../accounts/accounts";
import { apiCallBegan } from "../middlwares/api";

export const logout = (redirectToURL: string | null) => {
  localStorage.clear();
  const cookies = document.cookie.split("; ");
  cookies.forEach(cookie => {
    const domain = window.location.hostname.split(".");
    while (domain.length > 0) {
      const cookieBase =
        encodeURIComponent(cookie.split(";")[0].split("=")[0]) +
        "=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=" +
        domain.join(".") +
        " ;path=";
      const path = window.location.pathname.split("/");
      document.cookie = cookieBase + "/";
      while (path.length > 0) {
        document.cookie = cookieBase + path.join("/");
        path.pop();
      }
      domain.shift();
    }
  });
  if (redirectToURL !== null) {
    window.location.href = redirectToURL;
  }
  return logoutUser;
};
export const setToken = (token: string) => {
  localStorage.setItem("ucToken", token);
  return setTokenAction;
};

export const setShortToken = (shortToken: string) => {
  localStorage.setItem("id_token", shortToken);
};

export const login = (
  user: string,
  pass: string,
  captchaToken: string,
  code: string | undefined = undefined
) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.login}`,
        method: "post",
        data: {
          user,
          pass,
          captchaToken,
          code
        },
        onStart: loginStart,
        onSuccess: ({
          token,
          shortToken,
          user
        }: {
          token: string;
          shortToken: string;
          user: UserClaims;
        }) => (dispatch: AppDispatch) => {
          dispatch(loginSuccess());
          setShortToken(shortToken);
          dispatch(setToken(token));
          dispatch(setUserClaims(user));
          dispatch(setOrganization(user.organization as Organization));
          dispatch(authSuccess());
          history.push(amRoutes.dashboard);
        },
        onFailure: (error: any) => (dispatch: AppDispatch) => {
          const { errorCode } = getApplicationErrorFromRequest(error);
          if (errorCode === USER_CREDENTIALS_MISSING_2FA_CODE) {
            dispatch(show2FALoginField());
          } else {
            dispatch(loginFail(getErrorMessageFromRequest(error)));
            dispatch(authFail());
          }
        }
      })
    );
  };
};

export const checkAuth = () => {
  return (dispatch: AppDispatch) => {
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.thisUser}`,
        method: "get",
        onStart: startAuth,
        onSuccess: ({
          token,
          shortToken,
          user
        }: {
          token: string;
          shortToken: string;
          user: UserClaims;
        }) => (dispatch: AppDispatch) => {
          dispatch(loginSuccess());
          setShortToken(shortToken);
          dispatch(setToken(token));
          dispatch(setUserClaims(user));
          // @ts-ignore This action is not typed yet
          dispatch(setOrganization(user.selectedOrganization));
          dispatch(authSuccess());
        },
        onFailure: () => (dispatch: AppDispatch) => {
          localStorage.clear();
          dispatch(authFail());
        }
      })
    );
  };
};

export const handleSaveUserRoles = (id: number, roles: number[]) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.users}/${id}/roles`,
        method: "put",
        data: {
          rolesIds: roles
        },
        onSuccess: getUsers,
        onSuccessMessage: "Roles successfully saved!",
        onFailureMessage: "An error ocurred while saving user roles"
      })
    );
  };
};

export const changeUserAttribute = (
  key: string,
  value: any,
  successMessage: string,
  failureMessage: string
) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.thisUser}`,
        method: "put",
        data: {
          [key]: value
        },
        onSuccess: (response: { token: string; user: UserClaims }) => (dispatch: AppDispatch) => {
          dispatch(setToken(response.token));
          dispatch(setUserClaims(response.user));
          setTimeout(() => window.location.reload(), delayBeforeHideSuccessMessage);
        },
        onSuccessMessage: successMessage,
        onFailureMessage: failureMessage
      })
    );
  };
};

export const startResetPassword = (email: string) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.startPasswordReset}`,
        method: "post",
        data: email
      })
    );
  };
};

export const checkPasswordAgainstUC = (username: string, password: string) => {
  return new Promise((resolve, reject) => {
    axios
      .post(`${ucEndpointsUrls.login}`, {
        user: username,
        pass: password
      })
      .then(res => resolve(res))
      .catch(err => reject(err));
  });
};

// Note: This is not used
export const changeOrganizationsViewport = (selectedCity: any) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.thisOrganization}`,
        method: "put",
        data: {
          city: selectedCity
        },
        onSuccessMessage: "Viewport successfully changed!",
        onFailureMessage: "Error while changing viewport"
      })
    );
  };
};

export const updateUserSettings = ({
  defaultRegionId = undefined,
  defaultCityId = undefined
}: {
  defaultRegionId?: number | null;
  defaultCityId?: number | null;
}) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.usersSettings}`,
        method: "put",
        data: {
          defaultRegionId,
          defaultCityId
        },
        onSuccessMessage: "User settings successfully updated!",
        onFailureMessage: "Error while updating user settings",
        onSuccess: () => (dispatch: AppDispatch) => dispatch(getUserSettings())
      })
    );
  };
};

/**
 * Checks if given a set of userRoles, that user can add another one using the given role.
 *
 * @param {Role[]} userRoles     - Array of roles objects.
 * @param {string} role          - Role code (or name).
 */
export const canAddToRole = (userRoles: Role[], role: string) => {
  // @ts-ignore
  return userRoles.some(userRole => pencilerRoles.canOnlyAddUsersTo[userRole.code].includes(role));
};

/**
 * Checks if user can edit other users.
 *
 * @param {Role[]} userRoles     - Array of role objects.
 * @return {Boolean}    - Returns true if the user can edit other users.
 */
export const canEditUsers = (userRoles: Role[]) =>
  userRoles.some(userRole => pencilerRoles.canEditUsers.includes(userRole.code));

/**
 * Sends user's password reset request
 * @return {AnyActionOrThunk}
 */
export const handleResetPasswordRequest = (email: string) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.startPasswordReset}`,
        method: "post",
        data: {
          email
        },
        //CHECK: Should this be set? Or resetPasswordStart
        onStart: resetResetPasswordModal,
        onSuccess: (dispatch: AppDispatch) => {
          dispatch(resetResetPasswordModal);
          dispatch(
            statusMessages.showSuccessMessage("Password successfully reset!", "Redirecting...")
          );
          setTimeout(() => resetApp(), delayBeforeRedirect);
        },
        onFailure: (error: any) => (dispatch: AppDispatch) => {
          let message = getErrorMessageFromRequest(error);
          if (error.response.status === httpStatusCode.notFound) {
            message = "Email address or user not found";
          }
          dispatch(resetPasswordFailed(message));
        }
      })
    );
  };
};

/**
 * Sends delete user request.
 *
 * @param {number} id       - User ID to be deleted.
 * @return {AnyActionOrThunk}
 */
export const handleDeleteUser = (id: number) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.organizationsUsers}/${id}`,
        method: "delete",
        onSuccessMessage: "User successfully removed!",
        onSuccess: getUsers,
        onFailureMessage: "An error ocurred while deleting the user"
      })
    );
  };
};
/**
 * Sends delete invite request.
 *
 * @param {number} id       - User ID to be deleted.
 * @return {AnyActionOrThunk}
 */
export const handleCancelInvite = (id: number) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.inviteUser}/${id}`,
        method: "delete",
        onSuccessMessage: "Invitation successfully canceled!",
        onSuccess: getUsers,
        onFailureMessage: "An error ocurred while canceling the invitation"
      })
    );
  };
};

/**
 * Sends reinvite user request.
 *
 * @param {Number} id       - User ID to be deleted.
 * @return {AnyActionOrThunk}
 */
export const handleReinviteUser = (id: number) => {
  return (dispatch: AppDispatch) => {
    dispatch(
      apiCallBegan({
        url: `${ucEndpoints.renewInviteUser}/${id}`,
        method: "put",
        onSuccessMessage: "Invitation email successfully sent!",
        onSuccess: () => (dispatch: AppDispatch) => {
          dispatch(getUsers());
          dispatch(resetInviteUserToAccountModal());
        },
        onFailureMessage: "An error ocurred while trying to reinvite the user"
      })
    );
  };
};

/**
 * Sends delete account request
 * This method is intended for deleting the user's account
 *
 * @returns {AnyActionOrThunk}
 */
export const handleDeleteAccountFormSubmit = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.thisUser}`,
      method: "delete",
      onSuccessMessage: "Account successfully deleted!",
      onSuccess: () => (dispatch: AppDispatch) => {
        dispatch(resetApp());
      },
      onFailureMessage: "An error ocurred while trying to delete the account"
    })
  );

/**
 * Gets current user's ACL list
 *
 * @returns {AnyActionOrThunk}
 */
export const getAclList = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.aclList}`,
      method: "get",
      onStart: sendGetAclList,
      onSuccess: getAclListSuccess,
      onFailure: getAclListFail,
      onFailureMessage: "An error ocurred while getting user's permissions"
    })
  );

/**
 * Gets user types
 *
 * @returns {AnyActionOrThunk}
 */
export const getUserTypes = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.userTypes}`,
      method: "get",
      onSuccess: setUserTypes,
      onFailureMessage: "An error ocurred while getting user types"
    })
  );

/**
 * Gets user settings
 *
 * @returns {AnyActionOrThunk}
 */
export const getUserSettings = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.usersSettings}`,
      method: "get",
      onSuccess: setUserSettings,
      onFailureMessage: "An error occurred while getting user settings"
    })
  );

/**
 * Gets user available regions
 *
 * @returns {AnyActionOrThunk}
 */
export const getAvailableRegions = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.availableRegions}`,
      method: "get",
      onSuccess: setAvailableRegions
    })
  );

/**
 * Gets user available roles
 *
 * @returns {AnyActionOrThunk}
 */
export const getAvailableRoles = () => (dispatch: AppDispatch) =>
  dispatch(
    apiCallBegan({
      url: `${ucEndpoints.userRoles}`,
      method: "get",
      onSuccess: setAvailableRoles
    })
  );

/**
 * Renew user token
 *
 * @returns {AnyActionOrThunk}
 */
export const renewToken = ({
  regionId,
  organizationId
}: {
  regionId?: number;
  organizationId?: number;
}) => (dispatch: AppDispatch) => {
  let data = Object.assign({}, exists(regionId) ? { regionId } : null);
  data = Object.assign(data, exists(organizationId) ? { organizationId } : null);
  return dispatch(
    apiCallBegan({
      url: `${ucEndpoints.renewToken}`,
      method: "post",
      data,
      onSuccess: ({
        token,
        shortToken,
        user
      }: {
        token: string;
        shortToken: string;
        user: UserClaims;
      }) => (dispatch: AppDispatch) => {
        setShortToken(shortToken);
        dispatch(setToken(token));
        dispatch(setUserClaims(user));
        setTimeout(() => window.location.reload(), delayBeforeHideSuccessMessage);
      },
      onSuccessMessage: "Region successfully changed! Reloading...",
      onFailureMessage: "An error ocurred while renewing user token"
    })
  );
};

/**
 * Checks if user can perform the a specific action based on it's ACL list.
 *
 * @param {String} resourceCode
 * @param {String} actionCode
 * @param {String} ownership
 * @returns {bool}
 */
export const canPerformAction = (resourceCode: string, actionCode: string, ownership: string) => (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  const aclList = getState().users.aclList;
  return (
    exists(aclList[resourceCode]) &&
    aclList[resourceCode].some(
      action => action.code === actionCode && action.ownership === ownership
    )
  );
};

/**
 * Infer user system from it's roles
 *
 * @param {Role[]} roles       - List of roles
 * @returns {string[]}           - List of systems
 */
export const getSystemsFromUserRoles = (roles: Role[]) => {
  const systems: string[] = [];
  roles.forEach(role => (systems.includes(role.system) ? null : systems.push(role.system)));
  return systems;
};

/**
 * Check if user is coming from external IdP
 * Example: Azure AD
 *
 * @return {Boolean}                - True if user is external
 */
export const isExternal = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const { userClaims } = getState().users;
  return exists(userClaims) && exists(userClaims.external) && userClaims.external;
};
